import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Spacer,
  VStack,
  Text,
  HStack,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  SimpleGrid,
  useDisclosure,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { ColumnType, columnTypeToJSON } from "build-ts/enums";
import { useConfigurationContext } from "context/ConfigurationContext";
import CreateKGColumnComponent from "modules/kgcolumn/containers/KGColumnCreationForm";
import CustomModal from "components/modals/CustomModal";
import ListItemMeta from "modules/common/ListItemMeta";
import ConfigurationListFilterControl from "modules/configuration/components/ConfigurationListFilterControl";
import { listPanelScrollSx } from "utils/listScrollAreaStyles";
import { AppIcons } from "constants/icons";
import { iconForKgColumn, kgCreateModalTitle } from "modules/mkg/utils/ontologyEntityIcons";
import { useMatch } from "react-router-dom";

interface KGColumnListProps {
  kgColumnList: Header[];
  columnType: ColumnType;
  refreshKgColumns: () => Promise<Header[]>;
  splitDetailOpen: boolean;
}

function kgSectionTitle(columnType: ColumnType): string {
  switch (columnType) {
    case ColumnType.MEASURE:
      return "Measures";
    case ColumnType.ATTRIBUTE:
      return "Attributes";
    case ColumnType.TEMPORAL:
      return "Datetime";
    default:
      return columnTypeToJSON(columnType);
  }
}

const KGColumnList: React.FC<KGColumnListProps> = ({ kgColumnList, columnType, refreshKgColumns, splitDetailOpen }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const createKGColumnModal = useDisclosure();
  const [filteredKGColumns, setFilteredKGColumns] = useState<Header[]>([]);
  const { selectedKgColumn, setSelectedMetric, setSelectedBenchmark, handleEditColumnModal } =
    useConfigurationContext();
  const navigate = useNavigate();

  const measuresIdMatch = useMatch("/mkg/measures/:id");
  const attributesIdMatch = useMatch("/mkg/attributes/:id");
  const timeframesIdMatch = useMatch("/mkg/timeframes/:id");
  const duplicateReloadRef = useRef<string | null>(null);

  // auto-select a KG column when the URL contains the entity's :id
  useEffect(() => {
    const id =
      columnType === ColumnType.MEASURE
        ? measuresIdMatch?.params?.id
        : columnType === ColumnType.ATTRIBUTE
          ? attributesIdMatch?.params?.id
          : timeframesIdMatch?.params?.id;
    if (!id || !kgColumnList.length) return;
    if (duplicateReloadRef.current === id) return;
    const header = kgColumnList.find(h => h.id === id);
    if (!header) return;
    handleEditColumnModal(header);
    duplicateReloadRef.current = id;
  }, [kgColumnList, measuresIdMatch?.params?.id, attributesIdMatch?.params?.id, timeframesIdMatch?.params?.id]);

  const handleKGColumnClick = (kgColumn: Header) => {
    handleEditColumnModal(kgColumn);
    setSelectedMetric(null);
    setSelectedBenchmark(null);
    if (columnType === ColumnType.MEASURE) {
      navigate(`/mkg/measures/${kgColumn.id}`);
    } else if (columnType === ColumnType.ATTRIBUTE) {
      navigate(`/mkg/attributes/${kgColumn.id}`);
    } else if (columnType === ColumnType.TEMPORAL) {
      navigate(`/mkg/timeframes/${kgColumn.id}`);
    }
  };

  const hasActiveFilters = searchQuery.trim() !== "" || filteredKGColumns.length < kgColumnList.length;

  const filterListKindLabel =
    columnType === ColumnType.MEASURE
      ? "measures"
      : columnType === ColumnType.ATTRIBUTE
        ? "attributes"
        : "datetime columns";

  const handleOpenCreateKGColumn = () => {
    createKGColumnModal.onOpen();
  };

  const handleCloseCreateKGColumn = () => {
    createKGColumnModal.onClose();
  };

  const rowTypeIcon =
    columnType === ColumnType.MEASURE
      ? AppIcons.measure
      : columnType === ColumnType.ATTRIBUTE
        ? AppIcons.attribute
        : AppIcons.timeframe;

  return (
    <Box h="100%" minH={0} display="flex" flexDirection="column">
      {/* Search and actions bar */}
      <Box px={3} py={3} flexShrink={0}>
        <HStack spacing={3} alignItems="center" width="100%">
          <InputGroup flex="1" maxW="400px">
            <InputLeftElement pointerEvents="none">
              <Icon as={AppIcons.search} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              size="sm"
              borderRadius="md"
              bg="white"
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)",
              }}
            />
          </InputGroup>

          <Button
            onClick={() => handleOpenCreateKGColumn()}
            leftIcon={<Icon as={AppIcons.plus} boxSize={3} />}
            size="xs"
            aria-label="Create New KG Column"
            borderRadius="md"
            title="Create New KG Column"
            bg="gray.50"
            color="purple.700"
            _hover={{ bg: "purple.700", color: "white" }}
            variant="outline"
            borderWidth="2px"
            borderColor="purple.700"
            fontWeight="700"
            iconSpacing={1}
            cursor="pointer"
          >
            New
          </Button>
        </HStack>
      </Box>

      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        pl={4}
        pr={splitDetailOpen ? 6 : 4}
        pb={14}
        minHeight={0}
        sx={listPanelScrollSx(splitDetailOpen)}
      >
        <VStack spacing={2} align="stretch">
          <HStack justify="space-between" align="center" mx={1} p={0} w="full" minW={0} spacing={2}>
            <HStack spacing={2} align="center" flexShrink={0} minW={0}>
              {!splitDetailOpen &&
                (columnType === ColumnType.MEASURE ? (
                  <Icon as={AppIcons.measure} boxSize={6} color="gray.700" flexShrink={0} />
                ) : columnType === ColumnType.ATTRIBUTE ? (
                  <Icon as={AppIcons.attribute} boxSize={5} color="gray.700" flexShrink={0} />
                ) : (
                  <Icon as={AppIcons.timeframe} boxSize={5} color="gray.700" flexShrink={0} />
                ))}
              <Text
                fontSize="sm"
                fontWeight="700"
                color="gray.700"
                letterSpacing="tight"
                mb={0}
                noOfLines={splitDetailOpen ? 1 : undefined}
              >
                {kgSectionTitle(columnType)}
              </Text>
            </HStack>
            {!splitDetailOpen && <Box flex="1" minW={2} height="1px" bg="gray.300" mx={1} />}
            <ConfigurationListFilterControl
              headerList={kgColumnList}
              type={filterListKindLabel}
              searchQuery={searchQuery}
              splitDetailOpen={splitDetailOpen}
              onChange={setFilteredKGColumns}
              defaultSortOption="modified_desc"
            />
          </HStack>
          {filteredKGColumns.length > 0 ? (
            splitDetailOpen ? (
              filteredKGColumns.map(kgColumn => {
                const sel = selectedKgColumn?.id === kgColumn.id;
                return (
                  <Button
                    key={kgColumn.id}
                    variant="outline"
                    size="sm"
                    flexDirection="column"
                    alignItems="stretch"
                    justifyContent="flex-start"
                    borderColor={sel ? "purple.600" : "gray.200"}
                    _hover={{
                      bg: sel ? "purple.700" : "purple.50",
                      borderColor: "purple.400",
                      color: sel ? "white" : "gray.800",
                    }}
                    bg={sel ? "purple.700" : "white"}
                    color={sel ? "white" : "gray.800"}
                    borderRadius="lg"
                    fontSize="sm"
                    fontWeight={sel ? "600" : "400"}
                    height="auto"
                    minH="4.5rem"
                    py={3}
                    px={4}
                    whiteSpace="normal"
                    textAlign="left"
                    onClick={() => handleKGColumnClick(kgColumn)}
                  >
                    <Flex direction="column" align="stretch" flex={1} w="full" minH="full" minW={0}>
                      <HStack align="flex-start" spacing={3} w="full" minW={0} pt={0.5}>
                        <Icon
                          as={rowTypeIcon}
                          boxSize={4}
                          flexShrink={0}
                          color={sel ? "whiteAlpha.900" : "purple.600"}
                          mt={0.5}
                          aria-hidden
                        />
                        <Text
                          mb={0}
                          fontWeight={sel ? "600" : "500"}
                          noOfLines={2}
                          fontSize="md"
                          textAlign="left"
                          flex={1}
                          minW={0}
                        >
                          {kgColumn.name}
                        </Text>
                      </HStack>
                      <Spacer minH={2} />
                      <Box w="full" pt={1}>
                        <ListItemMeta header={kgColumn} selected={sel} />
                      </Box>
                    </Flex>
                  </Button>
                );
              })
            ) : (
              <SimpleGrid columns={3} spacing={3} w="full">
                {filteredKGColumns.map(kgColumn => {
                  const selected = selectedKgColumn?.id === kgColumn.id;
                  return (
                    <Button
                      key={kgColumn.id}
                      w="full"
                      h="auto"
                      minH="6rem"
                      py={4}
                      px={5}
                      borderRadius="2xl"
                      variant="outline"
                      borderWidth={selected ? "2px" : "1px"}
                      borderColor={selected ? "purple.600" : "gray.200"}
                      flexDirection="column"
                      alignItems="stretch"
                      justifyContent="flex-start"
                      bg={selected ? "purple.700" : "white"}
                      color={selected ? "white" : "gray.800"}
                      _hover={{
                        bg: selected ? "purple.700" : "purple.50",
                        borderColor: "purple.400",
                      }}
                      onClick={() => handleKGColumnClick(kgColumn)}
                    >
                      <Flex direction="column" align="stretch" flex={1} w="full" minH="full" minW={0}>
                        <HStack align="flex-start" spacing={3} w="full" minW={0} pt={1}>
                          <Icon
                            as={rowTypeIcon}
                            boxSize={5}
                            flexShrink={0}
                            color={selected ? "whiteAlpha.900" : "purple.600"}
                            mt={0.5}
                            aria-hidden
                          />
                          <Text
                            fontWeight="600"
                            fontSize="md"
                            noOfLines={2}
                            lineHeight="short"
                            textAlign="left"
                            mb={0}
                            flex={1}
                            minW={0}
                          >
                            {kgColumn.name}
                          </Text>
                        </HStack>
                        <Spacer minH={2} />
                        <Box w="full" pt={1}>
                          <ListItemMeta header={kgColumn} selected={selected} />
                        </Box>
                      </Flex>
                    </Button>
                  );
                })}
              </SimpleGrid>
            )
          ) : (
            <Center py={8}>
              <Text color="gray.500" textAlign="center" px={2}>
                {hasActiveFilters ? "No columns match your search or filters." : "No columns found."}
              </Text>
            </Center>
          )}
        </VStack>
      </Box>

      {/* Create KG Column Modal */}
      {createKGColumnModal.isOpen && (
        <CustomModal
          isOpen={createKGColumnModal.isOpen}
          onClose={handleCloseCreateKGColumn}
          title={
            <HStack spacing={3}>
              <Icon as={iconForKgColumn(columnType)} boxSize={6} color="purple.600" flexShrink={0} aria-hidden />
              <Text fontSize="lg" fontWeight="bold" mb={0}>
                {kgCreateModalTitle(columnType)}
              </Text>
            </HStack>
          }
          size="xl"
          maxWidth="800px"
        >
          <CreateKGColumnComponent
            onClose={handleCloseCreateKGColumn}
            colType={columnType}
            refreshKGColumns={refreshKgColumns}
          />
        </CustomModal>
      )}
    </Box>
  );
};

export default KGColumnList;
